import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspace } from '@/contexts/workspace-context';
import type { Channel } from '@/types/channel';
import type { Workspace } from '@/types/workspace';
import type { AnnouncementModalProps } from './announcement-modal';
import { announcementDismissKey } from './keys';

/**
 * Context handed to every announcement so its audience gate + actions can be
 * derived without each entry reaching into hooks itself.
 */
interface AnnouncementContext {
  activeWorkspace: Workspace | null;
  channels: Channel[];
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * A single registered announcement. `eligible` is the audience gate; `build`
 * produces the props for the generic AnnouncementModal. Keep one announcement
 * shown at a time — the renderer picks the first eligible, not-yet-dismissed
 * entry from this list.
 */
interface AnnouncementDefinition {
  id: string;
  eligible: (ctx: AnnouncementContext) => boolean;
  build: (ctx: AnnouncementContext) => Omit<AnnouncementModalProps, 'id' | 'eligible'>;
}

// ──────────────────────────────────────────────────────────────────────────
// The registry. Add future announcements here as new entries.
// (The gateway-announcement:v1 entry was retired once the Gateway shipped to
// production — its rollout pre-announcement was no longer needed.)
// ──────────────────────────────────────────────────────────────────────────
const ANNOUNCEMENTS: AnnouncementDefinition[] = [];

/** True when this announcement has already been dismissed once. */
function isDismissed(id: string): boolean {
  return localStorage.getItem(announcementDismissKey(id)) !== null;
}

/**
 * Returns the props for the first eligible, not-yet-dismissed announcement, or
 * null when there is nothing to show. One announcement is shown at a time.
 */
export function useActiveAnnouncement(): AnnouncementModalProps | null {
  const { activeWorkspace, channels } = useWorkspace();
  const navigate = useNavigate();

  return useMemo(() => {
    const ctx: AnnouncementContext = { activeWorkspace, channels, navigate };
    const active = ANNOUNCEMENTS.find(
      (announcement) =>
        !isDismissed(announcement.id) && announcement.eligible(ctx),
    );
    if (!active) return null;
    return { id: active.id, eligible: true, ...active.build(ctx) };
  }, [activeWorkspace, channels, navigate]);
}
